import "../../ChartBrand.css";
import "../ChartLegend.css";

import { useLayoutEffect, useRef, useState } from "react";

import { useChartVariant } from "../../ChartVariantContext";
import { ChartVariant, chunkLegendItems, getChartTheme, getLegendColumnCount } from "../../utils";
import { ChartLegendLabel } from "./ChartLegendLabel";

type ChartLegendEmphasis = "Primary" | "Secondary";

export interface ChartLegendBaseProps {
  /** Legend labels, in chart order. */
  labels: string[];
  /** Formats the value shown next to a label. */
  renderValue: (label: string) => string;
  /** The label's share of the total, as a formatted string. */
  renderPercentage: (label: string) => string;
  colours: string[];
  hasData: boolean;
  defaultValue?: string | null;
  showValues?: boolean;
  emphasis?: ChartLegendEmphasis;
  /** Corner radius (px) for the colour swatch — square for Hub, rounded for Seed. */
  keyBorderRadius?: number;
  /** Index of the segment currently being pointed at, from either the legend or the chart. */
  activeIndex?: number | null;
  /** Reports what the legend is being pointed at, so the chart can highlight it. */
  onActiveIndexChange: (index: number | null) => void;
  /**
   * The chart's resolved brand. Pass it whenever the chart resolved one itself — an
   * explicit `variant` on PieChart beats the provider, and re-reading the context here
   * would quietly put Hub type on a Seed chart.
   */
  variant?: ChartVariant;
}

/**
 * The legend: one row per segment, `swatch | label | % | value`.
 *
 * Each column is a CSS grid rather than a table, so the label column is the only one
 * that flexes — it takes the space the card actually has (`minmax(0, 1fr)`) instead of
 * forcing the legend wider than its card. Labels that don't fit are cut and recovered
 * on hover; see ChartLegendLabel.
 */
export const ChartLegendBase = ({
  labels,
  renderValue,
  renderPercentage,
  colours,
  hasData,
  defaultValue = null,
  showValues = true,
  emphasis = "Primary",
  keyBorderRadius = 2,
  activeIndex = null,
  onActiveIndexChange,
  variant,
}: ChartLegendBaseProps): JSX.Element => {
  const contextVariant = useChartVariant();
  const brand = getChartTheme(variant ?? contextVariant).variant;
  const showKeys = emphasis === "Primary";

  // Columns are only worth having if there is width for them, so the count follows the
  // space the card actually gives the legend rather than the number of items alone.
  const legendRef = useRef<HTMLDivElement>(null);
  const [legendWidth, setLegendWidth] = useState(0);

  useLayoutEffect(() => {
    const legend = legendRef.current;
    if (!legend) return undefined;

    setLegendWidth(legend.offsetWidth);
    const observer = new ResizeObserver(([entry]) => setLegendWidth(entry.contentRect.width));
    observer.observe(legend);
    return () => observer.disconnect();
  }, []);

  const columnCount = getLegendColumnCount(labels.length, legendWidth);
  const itemsPerColumn = Math.ceil(labels.length / columnCount);
  const columnClasses = ["ChartLegendColumn", showKeys ? "WithKey" : "", showValues ? "WithValue" : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={`ChartLegend ChartBrand_${brand} ${activeIndex !== null ? "HasActive" : ""}`} ref={legendRef}>
      {chunkLegendItems(labels, itemsPerColumn).map((columnLabels, columnIdx) => (
        <div className={columnClasses} key={columnLabels[0]}>
          {columnLabels.map((text, labelIdx) => {
            const idx = columnIdx * itemsPerColumn + labelIdx;
            return (
              <button
                className={`ChartLegendRow ${activeIndex === idx ? "Active" : ""}`}
                key={text}
                // Rows are focusable so the segment highlight is reachable without a pointer.
                type="button"
                onMouseEnter={() => onActiveIndexChange(idx)}
                onMouseLeave={() => onActiveIndexChange(null)}
                onFocus={() => onActiveIndexChange(idx)}
                onBlur={() => onActiveIndexChange(null)}
              >
                {showKeys && (
                  <div
                    className="ChartLegendBox"
                    style={{ backgroundColor: colours[idx], borderRadius: keyBorderRadius }}
                  />
                )}
                <ChartLegendLabel text={text} variant={brand} />
                <p className="body3 ChartLegendPercentage">{hasData ? renderPercentage(text) : defaultValue}</p>
                {showValues && (
                  <p
                    className={`body3 ${hasData ? "ChartLegendValue" : "ChartLegendNoValue"}`}
                    style={emphasis === "Secondary" ? { color: colours[idx] } : undefined}
                  >
                    {hasData ? renderValue(text) : defaultValue}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};
